import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Icon,
  Illustration,
  type IllustrationKind,
  InlineMessage,
  InputField,
  Label,
  MenuInput,
  type MenuInputOption,
  RadioButton,
  RadioButtonGroup,
  Typography,
  Upload,
  useUploadFile,
} from '@saviynt/design-system';

import { useInvertedLocalize } from '../../../shared/utils';
import { makeDataUri } from '../utils';
import {
  getNameIdFormatOptions,
  getPrincipalTypeOptions,
  resolveNameIdFormat,
} from '../utils/nameIdFormats';

import './MetadataFilePicker.css';

enum LabelKeys {
  entityDescriptorUrl = 'AUTH_MetadataFilePicker_entityDescriptorUrl',
  inputChoice = 'AUTH_MetadataFilePicker_inputChoice',
  urlInput = 'AUTH_MetadataFilePicker_urlInput',
  fileInput = 'AUTH_MetadataFilePicker_fileInput',
}

interface FileState {
  url?: string;
  data?: string;
}

interface Props {
  label: string;
  error: string;
  setError: (error: string) => void;
  hasValue: boolean;
  /** Called when a file is uploaded or cleared. Provides raw XML data for client-side parsing. */
  onFileChange: (args: FileState) => void;
  /** Called when the user clicks "Load" with a URL. Parent handles the backend call. */
  onLoadUrl: (url: string) => void;
  /** Whether the URL load is in progress (controlled by parent). */
  isUrlLoading: boolean;
  /** Whether the URL load succeeded (controlled by parent). */
  isUrlSuccess: boolean;
  /** The nameIDPolicyFormat value parsed from the metadata file (full URN or short key) */
  parsedNameIdFormat: string;
  /** Whether the metadata file contained a valid NameIDFormat */
  isNameIdFromFile: boolean;
  /** Called when the user selects a Name ID Format from the dropdown */
  onNameIdFormatChange: (value: string) => void;
  /** Called when the user selects a Principal Type (Transient only) */
  onPrincipalTypeChange: (value: string) => void;
  /** Called when the user enters a Principal Attribute (Transient only) */
  onPrincipalAttributeChange: (value: string) => void;
  /** Current principal type value */
  principalType: string;
  /** Current principal attribute value */
  principalAttribute: string;
  children?: React.ReactNode;
}

enum InputOption {
  url = 'url',
  file = 'file',
}

/**
 * Metadata File Picker
 * Handles both local file upload and URL input for SAML metadata.
 * - File path: reads file client-side and passes raw XML to parent via onFileChange
 * - URL path: delegates to parent via onLoadUrl (parent calls backend proxy)
 */
export function MetadataFilePicker({
  label,
  error,
  setError,
  hasValue,
  onFileChange,
  onLoadUrl,
  isUrlLoading,
  isUrlSuccess,
  parsedNameIdFormat,
  isNameIdFromFile,
  onNameIdFormatChange,
  onPrincipalTypeChange,
  onPrincipalAttributeChange,
  principalType,
  principalAttribute,
  children,
}: Props): JSX.Element {
  const l = useInvertedLocalize();
  const [inputChoice, setInputChoice] = useState(InputOption.url);
  const [localUrl, setLocalUrl] = useState('');
  const [otherError, setOtherError] = useState('');
  const [activeFileState, setActiveFileState] = useState<FileState>();
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  const isUrl = inputChoice === InputOption.url;

  // Name ID Format dropdown state
  const resolvedFormat = resolveNameIdFormat(parsedNameIdFormat);
  const nameIdFormatOptions = getNameIdFormatOptions(l).map((o) => ({
    ...o,
    isChecked: o.value === resolvedFormat,
  }));

  // When metadata loads, push the resolved or default value to the parent
  useEffect(() => {
    if (hasValue) {
      onNameIdFormatChange(resolvedFormat);
    }
    // Only react when hasValue or resolvedFormat changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasValue, resolvedFormat]);

  const handleNameIdFormatChange = (option?: MenuInputOption) => {
    onNameIdFormatChange(option?.value ?? resolvedFormat);
  };

  const principalTypeOptions = getPrincipalTypeOptions(l).map((o) => ({
    ...o,
    isChecked: o.value === principalType,
  }));

  const handlePrincipalTypeChange = (option?: MenuInputOption) => {
    onPrincipalTypeChange(option?.value ?? '');
  };

  const handleFileChange = (state: FileState) => {
    setActiveFileState(state);
    setHasUserInteracted(true);
    onFileChange(state);
  };

  const {
    handleFileSelect,
    handleFileDelete,
    uploadedFiles,
    maxFileSize,
    maxTotalFileSize,
    maxFileCount,
  } = useUploadFile(
    /* Max total File size */ 10,
    /* Max number of files */ 1,
    /* Max File Size in MB */ 10
  );

  const handleInputChoice = (value: InputOption) => {
    setInputChoice(value);

    if (!hasUserInteracted) {
      return;
    }

    // Swap errors between the two input modes
    setError(otherError);
    setOtherError(error);

    const updatedIsUrl = value === InputOption.url;

    if (updatedIsUrl) {
      // Switching to URL — clear file state from parent
      onFileChange({});
    } else {
      // Switching to file — restore previous file state
      onFileChange(activeFileState ?? {});
    }
  };

  const updateUrl = (value: string) => {
    setLocalUrl(value);
    setError('');
    setHasUserInteracted(true);
  };

  const handleLoadUrl = () => {
    try {
      // Validate URL format before delegating to parent
      // eslint-disable-next-line no-new
      new URL(localUrl);
    } catch {
      setError(l('Please enter a valid URL'));

      return;
    }

    onLoadUrl(localUrl);
  };

  const onLoadFile = (file: File) => {
    setError('');

    try {
      const reader = new FileReader();

      reader.onload = () => {
        const text = reader.result as string;

        if (text) {
          handleFileChange({ url: makeDataUri(text), data: text });
        }
      };

      reader.readAsText(file);
    } catch {
      setError(l('Failed to read metadata file'));
      handleFileChange({});
    }
  };

  useEffect(() => {
    if (uploadedFiles.length > 0) {
      onLoadFile(uploadedFiles[0].file);
    } else if (hasUserInteracted) {
      handleFileChange({});
    }
    // Only a change in `uploadedFiles` matters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedFiles]);

  return (
    <Box className='Auth_MetadataFilePicker'>
      <Box className='Auth_MetadataFilePicker_body'>
        <Box className='Auth_MetadataFilePicker_group'>
          <Typography kind='label' ariaHidden='true'>
            {/* ariaHidden b/c the RadioButtonGroup has an ariaLabel. Creating the association w/ IDs requires a DS change. */}
            {label}
          </Typography>
          <RadioButtonGroup
            ariaLabel={label}
            name={LabelKeys.inputChoice}
            value={inputChoice}
            onChange={(value) => handleInputChoice(value as InputOption)}>
            <RadioButton
              id={LabelKeys.urlInput}
              name={LabelKeys.urlInput}
              title={l('via URL')}
              description={l(
                'Fetch entity descriptor information from an entity descriptor URL.'
              )}
              FrontIllustration={
                <Illustration
                  kind={
                    /* Workaround for missing type, remove on next DS update */
                    'CreateApplication' as IllustrationKind
                  }
                />
              }
              value={InputOption.url}
              radioSelection={inputChoice}
            />
            <RadioButton
              id={LabelKeys.fileInput}
              name={LabelKeys.fileInput}
              title={l('via File Upload')}
              description={l(
                'Fetch entity descriptor information from a local file.'
              )}
              FrontIllustration={<Illustration kind='Documents' />}
              value={InputOption.file}
              radioSelection={inputChoice}
            />
          </RadioButtonGroup>
        </Box>
        {isUrl ? (
          <Box className='Auth_MetadataFilePicker_entityInput'>
            <InputField
              name={LabelKeys.entityDescriptorUrl}
              placeholder=''
              value={localUrl}
              setValue={updateUrl}
              isRequired={!hasValue}
              label={
                <Typography
                  kind='label'
                  htmlFor={LabelKeys.entityDescriptorUrl}>
                  {l('Entity Descriptor URL')}
                </Typography>
              }
              isCritical={!!error}
            />
            <Box className='Auth_MetadataFilePicker_loadButtonSlot'>
              <Button
                className='Auth_MetadataFilePicker_loadButton'
                isLoading={isUrlLoading}
                isDisabled={localUrl.length === 0}
                isSuccess={isUrlSuccess && !error}
                successText={l('Loaded')}
                onClick={handleLoadUrl}>
                {l('Load')}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box className='Auth_MetadataFilePicker_group'>
            <Box>
              {!hasValue && (
                <Box className='Auth_MetadataFilePicker_required'>
                  {/* eslint-disable-next-line react/jsx-no-literals, formatjs/no-literal-string-in-jsx -- It's an asterisk. No need to translate */}
                  <Box>*</Box>
                </Box>
              )}
              <Typography kind='label'>{l('Upload File')}</Typography>
            </Box>
            <Upload
              className='Auth_MetadataFilePicker_upload'
              kind='dropzone'
              isMultiple={false}
              onFileSelect={handleFileSelect}
              onFileDelete={handleFileDelete}
              uploadedFiles={uploadedFiles}
              acceptTypes='.xml'
              maxFileCount={maxFileCount}
              maxFileSize={maxFileSize}
              maxTotalFileSize={maxTotalFileSize}
            />
          </Box>
        )}
        {!!error && (
          <InlineMessage
            text={error}
            colorTheme='critical'
            size='small'
            leftIcon={<Icon kind='AlertCriticalFilled' />}
          />
        )}
        {hasValue && (
          <MenuInput
            className='Auth_MetadataFilePicker_nameIdFormat'
            label='AUTH_MetadataFilePicker_nameIdFormat'
            options={nameIdFormatOptions}
            onChange={handleNameIdFormatChange}
            isDisabled={isNameIdFromFile}
            isRequired={!isNameIdFromFile}
            inputLabel={
              <Label htmlFor='AUTH_MetadataFilePicker_nameIdFormat'>
                {l('Name ID Format')}
              </Label>
            }
          />
        )}
        {hasValue && !isNameIdFromFile && resolvedFormat === 'transient' && (
          <Box className='Auth_MetadataFilePicker_transientFields'>
            <MenuInput
              className='Auth_MetadataFilePicker_principalType'
              label='AUTH_MetadataFilePicker_principalType'
              options={principalTypeOptions}
              onChange={handlePrincipalTypeChange}
              isRequired
              inputLabel={
                <Label htmlFor='AUTH_MetadataFilePicker_principalType'>
                  {l('Principal Type')}
                </Label>
              }
            />
            <InputField
              name='AUTH_MetadataFilePicker_principalAttribute'
              placeholder=''
              value={principalAttribute}
              setValue={onPrincipalAttributeChange}
              label={
                <Typography
                  kind='label'
                  htmlFor='AUTH_MetadataFilePicker_principalAttribute'>
                  {l('Principal Attribute')}
                </Typography>
              }
            />
          </Box>
        )}
        {children}
      </Box>
    </Box>
  );
}
